/**
 * Base Notification Handler
 * Provides default implementations for all notification handler methods
 */
class BaseNotificationHandler {
  /**
   * Parse the nested JSON data field from notification
   * @param {Object} notification - The notification object
   * @returns {Object} Parsed data object
   */
  parseData(notification) {
    let parsedData = {};
    
    // If notification already has parsedData, use it
    if (notification.parsedData) {
      parsedData = notification.parsedData;
    } else if (notification.data) {
      // Try to parse the data field (it might be a JSON string)
      try {
        if (typeof notification.data === 'string') {
          parsedData = JSON.parse(notification.data);
          
          // Handle double-nested JSON strings
          if (parsedData.data && typeof parsedData.data === 'string') {
            try {
              const nestedData = JSON.parse(parsedData.data);
              parsedData = { ...parsedData, ...nestedData };
            } catch (e) {
              // Ignore nested parse errors
            }
          }
          
          // Handle body field that contains nested JSON (for comment_reply)
          if (parsedData.body && typeof parsedData.body === 'string') {
            try {
              const bodyData = JSON.parse(parsedData.body);
              parsedData = { ...parsedData, ...bodyData };
            } catch (e) {
              // Ignore body parse errors
            }
          }
        } else if (typeof notification.data === 'object') {
          parsedData = notification.data;
        }
      } catch (error) {
        console.warn('Failed to parse notification data:', error);
        parsedData = {};
      }
    }
    
    return parsedData;
  }

  /**
   * Get emoji/icon for the notification
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string} Emoji or icon
   */
  getIcon(notification, parsedData) {
    return "🔔";
  }

  /**
   * Get display message
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string} Display message
   */
  getMessage(notification, parsedData) {
    if (parsedData.content) return parsedData.content;
    if (parsedData.message) return parsedData.message;
    if (notification.message) return notification.message;
    return "New notification";
  }

  /**
   * Get notification title
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string} Notification title
   */
  getTitle(notification, parsedData) {
    if (parsedData.title) return parsedData.title;
    if (notification.title) return notification.title;
    return "Notification";
  }

  /**
   * Get navigation URL
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string|null} Action URL or null
   */
  getActionUrl(notification, parsedData) {
    if (parsedData.action_url) return parsedData.action_url;
    if (notification.action_url) return notification.action_url;
    return null;
  }

  /**
   * Get additional details array
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {Array<string>} Array of detail strings
   */
  getDetails(notification, parsedData) {
    return [];
  }

  /**
   * Get thumbnail image URL
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string|null} Thumbnail URL or null
   */
  getThumbnail(notification, parsedData) {
    // Generic image fallback
    if (parsedData.image && typeof parsedData.image === 'string' && parsedData.image.trim() !== '') {
      return parsedData.image;
    }
    if (notification.image && typeof notification.image === 'string' && notification.image.trim() !== '') {
      return notification.image;
    }
    return null;
  }

  /**
   * Handle click navigation
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @param {Function} navigate - React Router navigate function
   * @param {Function} onClose - Callback to close dropdown
   */
  handleClick(notification, parsedData, navigate, onClose) {
    const actionUrl = this.getActionUrl(notification, parsedData);
    if (actionUrl) {
      // Check if it's a relative URL or absolute
      if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
        window.location.href = actionUrl;
      } else {
        navigate(actionUrl);
      }
      if (onClose) onClose();
    }
  }

  /**
   * Get sender name
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string|null} Sender name or null
   */
  getSenderInfo(notification, parsedData) {
    if (parsedData.sender_name) return parsedData.sender_name;
    if (parsedData.sender?.name) return parsedData.sender.name;
    if (parsedData.user_name) return parsedData.user_name;
    if (notification.sender_Data?.name) return notification.sender_Data.name;
    return null;
  }

  /**
   * Get sender image URL
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string|null} Sender image URL or null
   */
  getSenderImage(notification, parsedData) {
    // Check notification.sender_Data first (from backend NotificationResource)
    const senderDataImage = notification.sender_Data?.image;
    if (senderDataImage && typeof senderDataImage === 'string' && senderDataImage.trim() !== '') {
      return senderDataImage;
    }
    // Check parsedData for sender image
    const parsedSenderImage = parsedData.sender_image;
    if (parsedSenderImage && typeof parsedSenderImage === 'string' && parsedSenderImage.trim() !== '') {
      return parsedSenderImage;
    }
    const parsedSenderObjImage = parsedData.sender?.image;
    if (parsedSenderObjImage && typeof parsedSenderObjImage === 'string' && parsedSenderObjImage.trim() !== '') {
      return parsedSenderObjImage;
    }
    // Check nested data
    const nestedSenderImage = parsedData.data?.sender?.image;
    if (nestedSenderImage && typeof nestedSenderImage === 'string' && nestedSenderImage.trim() !== '') {
      return nestedSenderImage;
    }
    return null;
  }

  /**
   * Get main notification text (sender name + action)
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {string} Main notification text
   */
  getMainText(notification, parsedData) {
    const senderName = this.getSenderInfo(notification, parsedData) || 'Someone';
    const message = this.getMessage(notification, parsedData);
    if (message && message !== 'New notification') {
      return message;
    }
    return `${senderName} sent you a notification`;
  }

  /**
   * Check if this notification type has action buttons
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {boolean} True if notification has action buttons
   */
  hasActionButtons(notification, parsedData) {
    return false;
  }
}

export default BaseNotificationHandler;

